import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * PRODUCTION INCIDENT REGISTER
 *
 * A permanent, admin-only record of incidents that touched real money, with
 * the exact database rows they reconcile to. Written so that months later
 * anyone can answer "what happened, who was affected, was the money squared
 * away, and can it happen again" without guessing.
 *
 * Rows live in mkt_incidents. They are never deleted (RLS forbids DELETE);
 * an incident is closed by moving its status, not by erasing it.
 */

type Incident = {
  id: string;
  ref: string;
  title: string;
  severity: string;
  status: string;
  category: string;
  summary: string;
  root_cause: string | null;
  customer_impact: string | null;
  resolution: string | null;
  deployment_note: string | null;
  affected_email: string | null;
  money_amount: number;
  provider_receipt: string | null;
  manual_grant_ref: string | null;
  ledger_id: string | null;
  payment_id: string | null;
  payment_event_id: string | null;
  duplicate_protection_verified: boolean;
  duplicate_protection_evidence: string | null;
  reconciliation_state: string;
  /**
   * Follow-ups are stored as free-form JSON. Early rows are plain strings and
   * later ones are objects like { at, action, detail }. They are typed as
   * unknown and formatted through describeFollowUp below, because dropping a
   * raw object into JSX throws "Objects are not valid as a React child" and
   * would take the whole admin panel down.
   */
  follow_ups: unknown[] | null;

  occurred_at: string | null;
  recovered_at: string | null;
  closed_at: string | null;
  created_at: string;
};

const money = (cents: number) => `$${((cents || 0) / 100).toFixed(2)}`;

const SEVERITY_STYLE: Record<string, string> = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-rose-100 text-rose-800'
};

const STATUS_STYLE: Record<string, string> = {
  open: 'bg-rose-100 text-rose-800',
  mitigated: 'bg-amber-100 text-amber-800',
  monitoring: 'bg-sky-100 text-sky-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-slate-200 text-slate-700'
};

const RECONCILIATION_TEXT: Record<string, string> = {
  pending_provider_transaction:
    'A credit was granted by hand. If the provider ever delivers the real transaction it will be matched to this grant and no second credit will be issued.',
  reconciled: 'The hand-granted credit has been matched to the real provider transaction. Money and entitlement agree.',
  not_applicable: 'No manual grant was involved.',
  unrecoverable:
    'The original delivery was never captured and the provider will not re-send. This grant will stay hand-made in the record.'
};

/**
 * Formats one follow-up entry for display. Entries arrive as plain strings or
 * as { at, action, detail } objects, and an object rendered directly in JSX
 * crashes React, so everything is reduced to text here.
 */
const describeFollowUp = (entry: unknown): string => {
  if (typeof entry === 'string') return entry;
  if (entry && typeof entry === 'object') {
    const e = entry as Record<string, unknown>;
    const action = String(e.action || '').replace(/_/g, ' ').trim();
    const detail = String(e.detail || '').trim();
    const when = e.at ? new Date(String(e.at)).toLocaleDateString() : '';
    const head = [action, when].filter(Boolean).join(' · ');
    if (head && detail) return `${head} — ${detail}`;
    return detail || head || JSON.stringify(entry);
  }
  return String(entry ?? '');
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
    <dd className="mt-1 text-sm text-slate-800">{children}</dd>
  </div>
);


const AdminIncidents: React.FC = () => {
  const [rows, setRows] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('mkt_incidents')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);
    setRows((data as Incident[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (row: Incident, status: string) => {
    setBusy(row.id);
    await supabase
      .from('mkt_incidents')
      .update({
        status,
        updated_at: new Date().toISOString(),
        closed_at: status === 'closed' || status === 'resolved' ? new Date().toISOString() : null
      })
      .eq('id', row.id);
    setBusy(null);
    load();
  };

  const openCount = rows.filter((r) => r.status !== 'resolved' && r.status !== 'closed').length;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Production incidents</h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            Permanent record of events that affected real customers or real money, each linked to the exact ledger,
            payment and event rows it reconciles to. Records are never deleted.
          </p>
        </div>
        <button
          onClick={load}
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="mt-4 text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          No incidents recorded.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-slate-600">
            {openCount} still open or monitoring · {rows.length - openCount} resolved
          </p>

          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-slate-900 px-2 py-0.5 font-mono text-xs font-semibold text-white">
                  {r.ref}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    STATUS_STYLE[r.status] || 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {r.status}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    SEVERITY_STYLE[r.severity] || 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {r.severity}
                </span>
                {r.money_amount > 0 && (
                  <span className="text-sm font-medium text-slate-700">{money(r.money_amount)}</span>
                )}
                {r.provider_receipt && (
                  <span className="text-xs text-slate-500">receipt {r.provider_receipt}</span>
                )}
                <span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</span>
              </div>

              <h3 className="mt-2 text-sm font-semibold text-slate-900">{r.title}</h3>
              <p className="mt-1 text-sm text-slate-700">{r.summary}</p>

              {r.affected_email && (
                <p className="mt-2 text-xs text-slate-500">Affected customer: {r.affected_email}</p>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    r.duplicate_protection_verified
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-rose-100 text-rose-800'
                  }`}
                >
                  {r.duplicate_protection_verified
                    ? 'Duplicate protection verified'
                    : 'Duplicate protection NOT verified'}
                </span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                  {r.reconciliation_state.replace(/_/g, ' ')}
                </span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={() => setOpen(open === r.id ? null : r.id)}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  {open === r.id ? 'Hide detail' : 'View full record'}
                </button>
                {r.status !== 'resolved' && r.status !== 'closed' && (
                  <button
                    onClick={() => setStatus(r, 'resolved')}
                    disabled={busy === r.id}
                    className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {busy === r.id ? 'Saving…' : 'Mark resolved'}
                  </button>
                )}
              </div>

              {open === r.id && (
                <dl className="mt-4 grid gap-4 border-t border-slate-200 pt-4 md:grid-cols-2">
                  {r.root_cause && <Field label="Root cause">{r.root_cause}</Field>}
                  {r.customer_impact && <Field label="Customer impact">{r.customer_impact}</Field>}
                  {r.resolution && <Field label="Resolution">{r.resolution}</Field>}
                  <Field label="Reconciliation">
                    {RECONCILIATION_TEXT[r.reconciliation_state] || r.reconciliation_state}
                  </Field>
                  {r.duplicate_protection_evidence && (
                    <Field label="Duplicate protection evidence">{r.duplicate_protection_evidence}</Field>
                  )}
                  {r.deployment_note && (
                    <Field label="Deployment note">
                      <span className="italic">“{r.deployment_note}”</span>
                    </Field>
                  )}
                  <Field label="Linked records">
                    <ul className="space-y-1 font-mono text-xs text-slate-600">
                      {r.manual_grant_ref && <li>grant ref · {r.manual_grant_ref}</li>}
                      {r.ledger_id && <li>credit ledger · {r.ledger_id}</li>}
                      {r.payment_id && <li>payment · {r.payment_id}</li>}
                      {r.payment_event_id && <li>payment event · {r.payment_event_id}</li>}
                    </ul>
                  </Field>
                  {Array.isArray(r.follow_ups) && r.follow_ups.length > 0 && (
                    <Field label="Follow-ups">
                      <ul className="list-disc space-y-1 pl-4">
                        {r.follow_ups.map((f, i) => (
                          <li key={i}>{describeFollowUp(f)}</li>
                        ))}
                      </ul>
                    </Field>
                  )}

                </dl>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default AdminIncidents;
